import * as React from "react";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import PillSearchField from "../../components/PillSearchField";
import SegmentedStatusFilter from "../../components/SegmentedStatusFilter";
import CountryFilterButton from "../../components/CountryFilterButton";
import PillButtonGroup from "../../components/PillButtonGroup";
import {
  fetchActiveLabTests,
  markOrder,
  fetchAnalysisDetails,
  markTestAsProcessing,
  fetchOrderCountries,
} from "../../Services/apiService";
import socketService from "../../Services/socketService";
import { getProgressStatusStyle } from "../../theme/statusColors";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDF_FONT, registerPdfFont } from "../../utils/pdfFont";
  import DownloadIcon from "@mui/icons-material/Download";
import PageHeaderIcon from "../../components/PageHeaderIcon";
import StatusPill from "../../components/StatusPill";
import { IconButton, Tooltip } from "@mui/material";

import DownloadDialog from "../../components/DownloadDialog"; // adjust path if needed


// Columns for Analysis page
const getColumns = (navigate, updateMarkedStatus, setSelectedOrder, setSelectedTest, setDownloadDialogOpen) => [
  {
    field: "order_number",
    headerName: "Order ID",
    minWidth: 80,
    flex: 0.8,
  },
  {
    field: "test_identifier",
    headerName: "Test ID",
    minWidth: 90,
    flex: 1,
  },
  {
    field: "customer_name",
    headerName: "Name",
    minWidth: 180,
    flex: 1.2,
  },
  {
    field: "returned_to_lab_date",
    headerName: "Date Received",
    minWidth: 120,
    flex: 0.9,
    renderCell: (params) => {
      if (!params.value) return "N/A";
      const date = new Date(params.value);
      return date.toLocaleDateString();
    },
  },
  {
    field: "progress_status",
    headerName: "Progress Status",
    minWidth: 130,
    flex: 1,
    renderCell: (params) => {
      const status = params.value;
      const displayStatus = !status || status === "" ? "Unknown" : status;

      const statusStyle = getProgressStatusStyle(displayStatus);

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            paddingLeft: 1,
          }}
        >
          <Chip
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    ...statusStyle.dot,
                  }}
                />
                {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
              </Box>
            }
            sx={{
              ...statusStyle,
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
            size="small"
          />
        </Box>
      );
    },
  },
  {
    field: "marked_status",
    headerName: "Mark Order",
    sortable: false,
    align: "center",
    headerAlign: "center",
    minWidth: 150,
    flex: 1,
    renderCell: (params) => {
      const status = params.value;
      const displayStatus =
        !status || status === ""
          ? "None"
          : status === "Empty Returned"
            ? "Invalid Test"
            : status;

      // Define colors and dot styles based on status
      const getStatusStyle = (status) => {
        if (status === "Empty Returned") {
          status = "Invalid Test";
        }

        switch (status) {
          case "None":
            return {
              backgroundColor: "#f5f5f5",
              color: "#666",
              border: "1px solid #ddd",
              dot: { backgroundColor: "#666", border: "none" },
            };
          case "Review":
            return {
              backgroundColor: "#fff8dc",
              color: "#000",
              border: "1px solid #f0d000",
              dot: { backgroundColor: "#f0d000", border: "none" },
            };
          case "Invalid Test":
            return {
              backgroundColor: "#ffe6e6",
              color: "#000",
              border: "1px solid #ff6b6b",
              dot: {
                backgroundColor: "transparent",
                border: "1.5px solid #ff6b6b",
              },
            };
          case "Unused":
            return {
              backgroundColor: "#e0f7fa",
              color: "#006064",
              border: "1px solid #006064",
              dot: { backgroundColor: "#006064", border: "none" },
            };
          default:
            return {
              backgroundColor: "#f5f5f5",
              color: "#666",
              border: "1px solid #ddd",
              dot: { backgroundColor: "#666", border: "none" },
            };
        }
      };

      const statusStyle = getStatusStyle(displayStatus);

      return (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={params.value || ""}
              onChange={(e) => {
                e.stopPropagation();
                console.log(
                  "Changing mark for order",
                  params.row.order_number,
                  "to",
                  e.target.value
                );
                updateMarkedStatus(params.row.test_identifier, e.target.value);
              }}
              displayEmpty
              variant="standard"
              disableUnderline
              renderValue={() => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Chip
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            ...statusStyle.dot,
                          }}
                        />
                        {displayStatus}
                      </Box>
                    }
                    sx={{
                      ...statusStyle,
                      fontSize: "0.75rem",
                      height: "24px",
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                    size="small"
                  />
                  <Box
                    sx={{
                      width: 0,
                      height: 0,
                      borderLeft: "4px solid transparent",
                      borderRight: "4px solid transparent",
                      borderTop: "4px solid #666",
                      ml: 0.5,
                    }}
                  />
                </Box>
              )}
              sx={{
                "& .MuiSelect-select": {
                  paddingLeft: 0,
                  paddingRight: "0 !important",
                  textAlign: "center",
                  border: "none",
                  outline: "none",
                },
                "& .MuiSelect-icon": {
                  display: "none",
                },
                "& fieldset": {
                  border: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="None">
                <Chip
                  label="None"
                  sx={{
                    backgroundColor: "#f5f5f5",
                    color: "#666",
                    border: "1px solid #ddd",
                    fontSize: "0.75rem",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                  size="small"
                />
              </MenuItem>
              <MenuItem value="Review">
                <Chip
                  label="Review"
                  sx={{
                    backgroundColor: "#fff8dc",
                    color: "#000",
                    border: "1px solid #f0d000",
                    fontSize: "0.75rem",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                  size="small"
                />
              </MenuItem>
              <MenuItem value="Empty Returned">
                <Chip
                  label="Invalid Test"
                  sx={{
                    backgroundColor: "#ffe6e6",
                    color: "#000",
                    border: "1px solid #ff6b6b",
                    fontSize: "0.75rem",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                  size="small"
                />
              </MenuItem>

              <MenuItem value="Unused">
                <Chip
                  label="Unused"
                  sx={{
                    backgroundColor: "#e0f7fa",
                    color: "#006064",
                    border: "1px solid #006064",
                    fontSize: "0.75rem",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                  size="small"
                />
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      );
    },
  },
{
  field: "auto_report",
  headerName: "",
  minWidth: 80,
  flex: 0.3,
  sortable: false,
  filterable: false,
  align: "center",
  headerAlign: "center",
  renderCell: (params) => {
    const isCompleted =
      params.row.progress_status === "Completed" ||
      params.row.progress_status === "Analysis Completed";

    return (
      <Tooltip
        title={
          isCompleted ? "Download Report" : "Report not available yet"
        }
      >
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!isCompleted}
            onClick={(e) => {
              e.stopPropagation();
              console.log(
                "Opening download dialog for",
                `${params.row.order_number}-${params.row.test_identifier}`
              );
              // Open the download dialog
              setSelectedOrder(params.row.order_number);
              setSelectedTest(params.row.test_identifier);
              setDownloadDialogOpen(true);
            }}
          >
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  },
},
];

function Analysis() {
  const navigate = useNavigate();
  const [progressStatus, setProgressStatus] = React.useState("");
  const [fulfillmentStatus, setFulfillmentStatus] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [countryOptions, setCountryOptions] = React.useState([]);
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [data, setData] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [fetchTrigger, setFetchTrigger] = React.useState(0);
  const [socketConnected, setSocketConnected] = React.useState(false);
  const [newAnalysisIds, setNewAnalysisIds] = React.useState(new Set());
  const [updatingRows, setUpdatingRows] = React.useState(new Set());
  const [successRows, setSuccessRows] = React.useState(new Set());
  const [failedRows, setFailedRows] = React.useState(new Set());
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [bulkDownloading, setBulkDownloading] = React.useState(false);

  const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [selectedTest, setSelectedTest] = React.useState(null);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 30,
  });

  // Predefined progress status options
  const progressStatusOptions = [
    "returned to lab",
    "processing",
    "analysis completed",
    "completed",
    "cancelled",
  ];

  // Debounce search input — resets page and clears selection
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setSelectedRows([]);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Country dropdown options
  React.useEffect(() => {
    fetchOrderCountries()
      .then((res) => setCountryOptions(res?.countries || []))
      .catch((err) => console.error("❌ Failed to fetch order countries:", err));
  }, []);

  // Server-side fetch from fetchActiveLabTests with filters
  React.useEffect(() => {
    let cancelled = false;
    const { page, pageSize } = paginationModel;
    setLoading(true);
    setError(null);
    fetchActiveLabTests(page + 1, pageSize, {
      searchQuery: debouncedSearch,
      fulfillmentStatus,
      progressStatus,
      country,
    })
      .then((res) => {
        if (cancelled) return;
        const items = (res?.data || res?.orders || []).map((item) => ({
          ...item,
          id: item.test_identifier,
        }));
        const total = res?.total ?? res?.totalCount ?? res?.totalItems ?? 0;
        setData(items);
        setTotalCount(total || items.length);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debouncedSearch, fulfillmentStatus, progressStatus, country, paginationModel.page, paginationModel.pageSize, fetchTrigger]);

  // Function to update marked status using API
  const updateMarkedStatus = React.useCallback(
    async (testIdentifier, newStatus) => {
      try {
        console.log(
          `🔄 Updating marked status for ${testIdentifier} to: ${newStatus}`
        );

        const rowData = data.find(
          (item) => item.test_identifier === testIdentifier
        );
        if (!rowData) {
          console.error(
            "❌ Row data not found for test identifier:",
            testIdentifier
          );
          return;
        }

        const { order_number, test_identifier } = rowData;

        setUpdatingRows((prev) => new Set([...prev, testIdentifier]));
        setSuccessRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(testIdentifier);
          return newSet;
        });
        setFailedRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(testIdentifier);
          return newSet;
        });

        await markOrder(order_number, test_identifier, newStatus);

        setData((prevData) =>
          prevData.map((item) =>
            item.test_identifier === testIdentifier
              ? { ...item, marked_status: newStatus || null }
              : item
          )
        );

        setSuccessRows((prev) => new Set([...prev, testIdentifier]));
        console.log(
          "✅ Successfully updated marked status for:",
          testIdentifier
        );

        setTimeout(() => {
          setSuccessRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(testIdentifier);
            return newSet;
          });
        }, 3000);
      } catch (error) {
        console.error("❌ Error updating marked status:", error);

        setFailedRows((prev) => new Set([...prev, testIdentifier]));

        setTimeout(() => {
          setFailedRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(testIdentifier);
            return newSet;
          });
        }, 5000);
      } finally {
        setUpdatingRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(testIdentifier);
          return newSet;
        });
      }
    },
    [data]
  );

  // Handle new analysis items from socket — prepend to visible list
  const handleNewAnalysis = React.useCallback((newAnalysisArray) => {
    console.log(
      "🧪 [ANALYSIS] Handling new analysis item(s):",
      newAnalysisArray
    );

    const analysisItems = Array.isArray(newAnalysisArray)
      ? newAnalysisArray
      : [newAnalysisArray];

    analysisItems.forEach((newAnalysis) => {
      const transformedNewAnalysis = {
        ...newAnalysis,
        id: newAnalysis.test_identifier,
      };

      console.log(
        "🧪 [ANALYSIS] Processing analysis item:",
        transformedNewAnalysis
      );

      setData((prevData) => {
        const analysisExists = prevData.some(
          (item) => item.test_identifier === newAnalysis.test_identifier
        );
        if (analysisExists) {
          console.log(
            "⚠️ [ANALYSIS] Analysis item already exists, skipping...",
            newAnalysis.test_identifier
          );
          return prevData;
        }

        const updatedData = [transformedNewAnalysis, ...prevData];
        console.log(
          "✅ [ANALYSIS] Added new analysis item to top, new count:",
          updatedData.length
        );
        return updatedData;
      });

      setNewAnalysisIds((prevNewIds) => {
        const newSet = new Set([...prevNewIds, newAnalysis.test_identifier]);
        console.log(
          "🟢 [ANALYSIS] Marked analysis as new for highlighting:",
          newAnalysis.test_identifier
        );
        return newSet;
      });

      setTimeout(() => {
        setNewAnalysisIds((prevNewIds) => {
          const newSet = new Set(prevNewIds);
          newSet.delete(newAnalysis.test_identifier);
          console.log(
            "🔄 [ANALYSIS] Removed highlight for analysis:",
            newAnalysis.test_identifier
          );
          return newSet;
        });
      }, 5000);
    });
  }, []);

  // Socket setup for real-time updates
  React.useEffect(() => {
    let socket = socketService.connect();
    setSocketConnected(socketService.getStatus());

    if (!socket || !socket.connected) {
      console.log("⚠️ Socket was not connected, forcing reconnect...");
      socket = socketService.connect();
    }

    if (socket) {
      socket.on("connect", () => {
        setSocketConnected(true);
        console.log("🟢 Socket connected for Analysis component");
      });

      socket.on("disconnect", () => {
        setSocketConnected(false);
        console.log("🔴 Socket disconnected for Analysis component");
      });

      socket.on("connect_error", () => {
        setSocketConnected(false);
        console.log("❌ Socket connection error for Analysis component");
      });
    }

    socketService.onNewAnalysis(handleNewAnalysis);

    return () => {
      socketService.offNewAnalysis();
    };
  }, [handleNewAnalysis]);

  // Get columns with navigation function and update callback
  const columns = React.useMemo(
    () => getColumns(navigate, updateMarkedStatus, setSelectedOrder, setSelectedTest, setDownloadDialogOpen),
    [navigate, updateMarkedStatus, setSelectedOrder, setSelectedTest, setDownloadDialogOpen]
  );

  // Export function to download Excel file
  const handleExportToExcel = () => {
    const exportData = data.map((item) => ({
      "Order ID": item.order_number || "",
      "Test ID": item.test_identifier || "",
      "Name": item.customer_name || "",
      "Date Received": item.returned_to_lab_date
        ? new Date(item.returned_to_lab_date).toLocaleDateString()
        : "N/A",
      "Progress Status": (() => {
        const status = item.progress_status;
        return !status || status === "" ? "Unknown" : status;
      })(),
      "Mark Order": (() => {
        const status = item.marked_status;
        return !status || status === ""
          ? "None"
          : status === "Empty Returned"
            ? "Invalid Test"
            : status;
      })(),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Analysis");

    const colWidths = Object.keys(exportData[0] || {}).map((key) => {
      const maxLength = Math.max(
        key.length,
        ...exportData.map((row) => String(row[key] || "").length)
      );
      return { wch: Math.min(maxLength + 2, 30) };
    });
    ws["!cols"] = colWidths;

    const fileName = `analysis_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Function to generate PDF for a single form
  const generateSingleFormPDF = (
    formData,
    orderId,
    testId,
    doc,
    isFirstPage = true
  ) => {
    const pageWidth = doc.internal.pageSize.width;

    if (!isFirstPage) {
      doc.addPage();
    }

    doc.setFontSize(18);
    doc.setFont(PDF_FONT, "bold");
    const title = "Sibolab Auswertung";
    const titleWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - titleWidth) / 2;
    doc.text(title, titleX, 20);

    const currentDate = new Date().toLocaleDateString("de-DE");
    let formSubmissionDate = formData?.submissionTimestamp
      ? new Date(formData.submissionTimestamp).toLocaleDateString("de-DE")
      : formData?.metadata?.created_at
        ? new Date(formData.metadata.created_at).toLocaleDateString("de-DE")
        : formData?.test_information?.date_created
          ? new Date(formData.test_information.date_created).toLocaleDateString(
              "de-DE"
            )
          : formData?.created_at
            ? new Date(formData.created_at).toLocaleDateString("de-DE")
            : currentDate;

    const infoFields = [
      [
        "Order Nummer:",
        formData.test_information?.order_number || orderId || "",
      ],
      ["Test ID:", formData.test_information?.test_identifier || testId || ""],
      ["Geburtsdatum:", formData.mandatory_information?.date_of_birth || ""],
      ["Datum Probeentnahme:", formSubmissionDate],
      ["Datum Auswertung:", currentDate],
      ["Substrat:", formData.test_information?.substrate || ""],
    ];

    const infoTableStartY = 35;
    autoTable(doc, {
      body: infoFields,
      startY: infoTableStartY,
      margin: { left: 15, right: 15 },
      theme: "grid",
      styles: {
        font: PDF_FONT,
        fontSize: 11,
        cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        valign: "middle",
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: "bold" },
        1: { cellWidth: pageWidth - 80 },
      },
    });

    const tableColumns = ["Probenummer", "H2 (ppm)", "CH4 (ppm)", "CO2 (%)"];
    const tableRows = Array.from({ length: 10 }, (_, i) => [
      (i + 1).toString(),
      "",
      "",
      "",
    ]);

    const analysisTableStartY = doc.lastAutoTable.finalY + 10;
    const usableWidth = pageWidth - 30;
    const columnWidth = usableWidth / tableColumns.length;

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: analysisTableStartY,
      margin: { left: 15, right: 15 },
      styles: {
        font: PDF_FONT,
        fontSize: 10,
        cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        textColor: [0, 0, 0],
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        font: PDF_FONT,
        fontStyle: "bold",
        lineWidth: 0.4,
      },
      columnStyles: {
        0: { cellWidth: columnWidth },
        1: { cellWidth: columnWidth },
        2: { cellWidth: columnWidth },
        3: { cellWidth: columnWidth },
      },
      theme: "grid",
      pageBreak: "avoid",
    });

    const notesY = doc.lastAutoTable.finalY + 15;
    const notesHeight = 30;
    const notesMargin = 15;
    const notesWidth = pageWidth - notesMargin * 2;

    doc.setDrawColor(0, 0, 0);
    doc.rect(notesMargin, notesY, notesWidth, notesHeight);

    doc.setFont(PDF_FONT, "bold");
    doc.setFontSize(11);
    doc.text("Notizen:", notesMargin + 2, notesY - 2);
  };

  // Bulk download function
  const handleBulkDownload = async () => {
    if (selectedRows.length === 0) {
      alert("Please select at least one row to download.");
      return;
    }

    try {
      setBulkDownloading(true);
      console.log(
        `🚀 Starting bulk download for ${selectedRows.length} selected entries...`
      );

      const itemsToMarkAsProcessing = selectedRows.map((row) => ({
        orderId: row.order_number.toString(),
        testId: row.test_identifier,
      }));

      try {
        console.log(
          "🔄 Marking tests as processing before bulk PDF download..."
        );
        await markTestAsProcessing(itemsToMarkAsProcessing);
        console.log("✅ All tests marked as processing successfully");
      } catch (error) {
        console.error("❌ Error marking tests as processing:", error);
        console.log("⚠️ Continuing with PDF generation despite API error");
      }

      const formsData = [];
      for (let i = 0; i < selectedRows.length; i++) {
        const row = selectedRows[i];
        const orderId = row.order_number.toString();
        const testId = row.test_identifier;

        console.log(
          `📋 Fetching data for ${i + 1}/${selectedRows.length}: Order ${orderId}, Test ${testId}`
        );

        try {
          const formData = await fetchAnalysisDetails(orderId, testId);
          formsData.push({
            orderId,
            testId,
            formData,
            success: true,
          });
        } catch (error) {
          console.error(
            `❌ Error fetching data for Order ${orderId}, Test ${testId}:`,
            error
          );
          formsData.push({
            orderId,
            testId,
            formData: null,
            success: false,
            error: error.message,
          });
        }
      }

      const doc = new jsPDF();
      await registerPdfFont(doc);
      doc.setTextColor(0, 0, 0);

      let isFirstPage = true;
      const successfulForms = formsData.filter((item) => item.success);

      if (successfulForms.length === 0) {
        alert("No forms could be loaded. Please try again.");
        return;
      }

      for (const { orderId, testId, formData } of successfulForms) {
        generateSingleFormPDF(formData, orderId, testId, doc, isFirstPage);
        isFirstPage = false;
      }

      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `Sibolab_Bulk_Entry_Forms_${timestamp}_${successfulForms.length}_forms.pdf`;

      doc.save(filename);

      setSelectedRows([]);
    } catch (error) {
      console.error("❌ Error in bulk download:", error);
      alert(`Error during bulk download: ${error.message}`);
    } finally {
      setBulkDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Processing Overlay */}
      {bulkDownloading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Downloading {selectedRows.length} Entry Forms
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we generate and download your PDF files...
          </Typography>
        </Box>
      )}

      {/* Top row: title + live indicator */}
      <Box sx={{ px: 1, py: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
        <PageHeaderIcon src="/icons/ic_analysis.svg" />
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Analysis
        </Typography>

        <StatusPill connected={socketConnected} />

        <Button
          variant="contained"
          onClick={handleExportToExcel}
          disabled={loading || data.length === 0}
          sx={{ ml: "auto", textTransform: "none", borderRadius: 1.5, px: 2.5, py: 0.5, minHeight: 0, fontSize: "0.8125rem", backgroundColor: "#163666", color: "#fff", "&:hover": { backgroundColor: "#112B51" } }}
        >
          Export
        </Button>
      </Box>

      {/* Search + filters + actions row */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          rowGap: 1.5,
          flexWrap: "nowrap",
          alignItems: "center",
          mx: 1,
          mb: 2,
          p: 1.5,
          backgroundColor: "#FFFFFF",
          border: "1px solid #e5e7eb",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ width: 300, flexShrink: 0 }}>
          <PillSearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Enter order ID or test ID"
            resultCount={totalCount}
            loading={loading}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <SegmentedStatusFilter
            value={progressStatus}
            onChange={(value) => {
              setProgressStatus(value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
              setSelectedRows([]);
            }}
            options={progressStatusOptions.map((opt) => ({ value: opt, label: opt.charAt(0).toUpperCase() + opt.slice(1) }))}
            disabled={loading}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <SegmentedStatusFilter
            value={fulfillmentStatus}
            onChange={(value) => {
              setFulfillmentStatus(value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
              setSelectedRows([]);
            }}
            options={[{ value: "fulfilled", label: "Fulfilled" }, { value: "unfulfilled", label: "Unfulfilled" }]}
            disabled={loading}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <CountryFilterButton
            value={country}
            onChange={(value) => {
              setCountry(value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
              setSelectedRows([]);
            }}
            options={countryOptions}
            disabled={loading}
          />
        </Box>

        <PillButtonGroup sx={{ ml: "auto", flexShrink: 0 }}>
          <Button
            variant="text"
            disabled={selectedRows.length === 0 || bulkDownloading}
            onClick={handleBulkDownload}
            sx={{ px: 1.25, minWidth: 200, whiteSpace: "nowrap" }}
          >
            {bulkDownloading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Downloading...
              </Box>
            ) : selectedRows.length === 0 ? (
              "Download Entry Forms"
            ) : (
              `Download ${selectedRows.length} Entry Forms`
            )}
          </Button>
        </PillButtonGroup>
      </Box>

      {/* Data Table */}
      <Box sx={{ mx: 1, mb: 2, backgroundColor: "#FFFFFF", border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
        {error ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              minHeight: 400,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              Error loading analysis data
            </Typography>
            <Typography color="error" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setFetchTrigger((t) => t + 1)}
            >
              Retry
            </Button>
          </Box>
        ) : (
              <DataGrid
                rows={data}
                columns={columns}
                autoHeight
                rowHeight={40}
                columnHeaderHeight={40}
                onRowClick={(params) => navigate(`/analysis/${params.row.order_number}/${params.row.test_identifier}`)}
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) => {
                  console.log(
                    "📄 Analysis page changed to:",
                    newModel.page,
                    "pageSize:",
                    newModel.pageSize
                  );
                  setPaginationModel(newModel);
                }}
                paginationMode="server"
                rowCount={totalCount}
                loading={loading}
                pageSizeOptions={[30, 50, 100]}
                checkboxSelection
                onRowSelectionModelChange={(newSelection) => {
                  console.log(
                    "📥 Selection changed:",
                    newSelection,
                    "Type:",
                    typeof newSelection
                  );

                  let selectionArray = [];
                  if (Array.isArray(newSelection)) {
                    selectionArray = newSelection;
                  } else if (newSelection && newSelection.ids) {
                    selectionArray = Array.from(newSelection.ids);
                  }

                  console.log("📥 Selection array:", selectionArray);
                  console.log("📥 Selection length:", selectionArray.length);

                  const selectedRowsData = data.filter((item) =>
                    selectionArray.includes(item.id)
                  );
                  setSelectedRows(selectedRowsData);
                  console.log("🎯 Selected rows data:", selectedRowsData);
                  console.log(
                    "🎯 Selected rows count:",
                    selectedRowsData.length
                  );
                }}
                disableColumnSelector
                disableDensitySelector
                getRowClassName={(params) => {
                  const testId = params.row.test_identifier;
                  const classes = [];

                  if (newAnalysisIds.has(testId)) {
                    classes.push("new-analysis-highlight");
                  }

                  if (successRows.has(testId)) {
                    classes.push("success-row-highlight");
                  }

                  if (failedRows.has(testId)) {
                    classes.push("failure-row-highlight");
                  }

                  if (updatingRows.has(testId)) {
                    classes.push("updating-row-highlight");
                  }

                  return classes.join(" ");
                }}
                sx={{
                  border: "none",
                  borderRadius: 0,
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f5f5f5",
                  },
                  // Remove blue focus borders from cells
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                    border: "none !important",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                    border: "none !important",
                  },
                  "& .MuiDataGrid-columnHeader:focus": {
                    outline: "none !important",
                  },
                  "& .MuiDataGrid-columnHeader:focus-within": {
                    outline: "none !important",
                  },
                  // Remove blue selection border from rows
                  "& .MuiDataGrid-row:focus": {
                    outline: "none !important",
                  },
                  "& .MuiDataGrid-row:focus-within": {
                    outline: "none !important",
                  },
                  // Add pointer cursor for rows
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                  "& .MuiDataGrid-row:hover": {
                    cursor: "pointer",
                  },
                  // Custom styling for new analysis rows with green animation
                  "& .new-analysis-highlight": {
                    backgroundColor: "rgba(76, 175, 80, 0.1) !important",
                    borderLeft: "3px solid #4caf50",
                    animation:
                      "fadeInGreen 0.5s ease-in-out, fadeOutGreen 4.5s ease-in-out 0.5s forwards",
                    transition: "all 0.3s ease",
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid rgba(76, 175, 80, 0.2)",
                    },
                  },
                  "& .new-analysis-highlight:hover": {
                    backgroundColor: "rgba(76, 175, 80, 0.15) !important",
                  },
                  // Success row styling (green background)
                  "& .success-row-highlight": {
                    backgroundColor: "rgba(46, 125, 50, 0.15) !important",
                    borderLeft: "4px solid #2e7d32",
                    animation: "successPulse 0.6s ease-in-out",
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid rgba(46, 125, 50, 0.3)",
                      color: "#1b5e20",
                    },
                  },
                  "& .success-row-highlight:hover": {
                    backgroundColor: "rgba(46, 125, 50, 0.2) !important",
                  },
                  // Failure row styling (red background)
                  "& .failure-row-highlight": {
                    backgroundColor: "rgba(211, 47, 47, 0.15) !important",
                    borderLeft: "4px solid #d32f2f",
                    animation: "failurePulse 0.6s ease-in-out",
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid rgba(211, 47, 47, 0.3)",
                      color: "#c62828",
                    },
                  },
                  "& .failure-row-highlight:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.2) !important",
                  },
                  // Updating row styling (yellow/orange background)
                  "& .updating-row-highlight": {
                    backgroundColor: "rgba(255, 152, 0, 0.1) !important",
                    borderLeft: "4px solid #ff9800",
                    animation: "updatingPulse 1.5s ease-in-out infinite",
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
                    },
                  },
                  // Animation keyframes
                  "@keyframes fadeInGreen": {
                    "0%": {
                      backgroundColor: "rgba(76, 175, 80, 0.25)",
                      transform: "scale(1.01)",
                    },
                    "100%": {
                      backgroundColor: "rgba(76, 175, 80, 0.1)",
                      transform: "scale(1)",
                    },
                  },
                  "@keyframes fadeOutGreen": {
                    "0%": {
                      backgroundColor: "rgba(76, 175, 80, 0.1)",
                      borderLeft: "3px solid #4caf50",
                    },
                    "100%": {
                      backgroundColor: "transparent",
                      borderLeft: "3px solid transparent",
                    },
                  },
                  "@keyframes successPulse": {
                    "0%": {
                      backgroundColor: "rgba(46, 125, 50, 0.25)",
                      transform: "scale(1.005)",
                    },
                    "50%": {
                      backgroundColor: "rgba(46, 125, 50, 0.2)",
                    },
                    "100%": {
                      backgroundColor: "rgba(46, 125, 50, 0.15)",
                      transform: "scale(1)",
                    },
                  },
                  "@keyframes failurePulse": {
                    "0%": {
                      backgroundColor: "rgba(211, 47, 47, 0.25)",
                      transform: "scale(1.005)",
                    },
                    "50%": {
                      backgroundColor: "rgba(211, 47, 47, 0.2)",
                    },
                    "100%": {
                      backgroundColor: "rgba(211, 47, 47, 0.15)",
                      transform: "scale(1)",
                    },
                  },
                  "@keyframes updatingPulse": {
                    "0%": {
                      backgroundColor: "rgba(255, 152, 0, 0.1)",
                    },
                    "50%": {
                      backgroundColor: "rgba(255, 152, 0, 0.2)",
                    },
                    "100%": {
                      backgroundColor: "rgba(255, 152, 0, 0.1)",
                    },
                  },
                }}
              />
        )}
      </Box>

      {/* Download Dialog */}
      <DownloadDialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        orderId={selectedOrder}
        testId={selectedTest}
      />
    </Box>
  );
}

export default Analysis;
